import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, loginRequest } from "@/config/authConfig";

const RETRY_DELAY_MS = 400;
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// De-dupe concurrent callers — several queries fire at once on load, and we don't want to kick off
// multiple parallel silent renewals against the SSO session (a common source of transient failures).
let inFlight: Promise<string | null> | null = null;

async function acquireSilently(forceRefresh: boolean): Promise<string> {
  const account = msalInstance.getActiveAccount()!;
  const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh });
  return response.accessToken;
}

/**
 * Interactive re-auth — used ONLY when the session genuinely needs it (InteractionRequiredAuthError).
 * With a live SSO session this redirect round-trips silently (no credentials, no account picker) and
 * returns the user to the app; only a truly dead session shows the sign-in page. It never logs the
 * user out, so the confusing "which account do you want to sign out of" screen can no longer appear.
 */
async function redirectToReauth(): Promise<void> {
  const account = msalInstance.getActiveAccount() ?? undefined;
  try {
    await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
  } catch {
    // interaction_in_progress — a redirect is already underway; ignore.
  }
}

async function resolveToken(): Promise<string | null> {
  if (!msalInstance.getActiveAccount()) return null;

  try {
    return await acquireSilently(false);
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await redirectToReauth();
      return null;
    }

    // Transient silent failure (e.g. iframe / monitor-window timeout when renewing against the SSO
    // session). Retry once with a fresh renewal — this is effectively what a manual page reload does,
    // and it usually succeeds with no redirect at all.
    try {
      await delay(RETRY_DELAY_MS);
      return await acquireSilently(true);
    } catch (retryError) {
      if (retryError instanceof InteractionRequiredAuthError) {
        await redirectToReauth();
      }
      return null;
    }
  }
}

export const authService = {
  getAccessToken: async (): Promise<string | null> => {
    if (inFlight) return inFlight;
    inFlight = resolveToken();
    try {
      return await inFlight;
    } finally {
      inFlight = null;
    }
  },

  isAuthenticated: (): boolean => msalInstance.getActiveAccount() !== null,
};
